// Läser av en inklistrad eller uppladdad mailtråd och delar upp den i enskilda
// mail. Regexparsning klarar inte spannet av Outlook-, Gmail- och
// vidarebefordringsformat (svenska och engelska rubriker, citerad historik,
// signaturer), så tolkningen görs av modellen med ett schema som garanterar
// att svaret går att importera rakt av.
import { callClaudeJSON } from '../../utils/claudeProxy';

const THREAD_SCHEMA = {
  type: 'object',
  properties: {
    messages: {
      type: 'array',
      description: 'Ett objekt per mail i tråden, äldst först.',
      items: {
        type: 'object',
        properties: {
          date:      { type: 'string', description: 'Datum i formatet YYYY-MM-DD. Tom sträng om det inte går att utläsa.' },
          time:      { type: 'string', description: 'Klockslag HH:MM om det framgår, annars tom sträng.' },
          direction: { type: 'string', enum: ['out', 'in'], description: "'out' = skickat från TIKAB, 'in' = skickat av kunden." },
          fromName:  { type: 'string' },
          fromEmail: { type: 'string' },
          toNames:   { type: 'array', items: { type: 'string' }, description: 'Namn (eller adress) på mottagarna.' },
          toEmails:  { type: 'array', items: { type: 'string' } },
          subject:   { type: 'string' },
          summary:   { type: 'string', description: 'En mening på svenska om vad mailet gällde.' },
          body:      { type: 'string', description: 'Mailets brödtext, rensad från signatur, citerad historik och friskrivningar.' },
        },
        required: ['date', 'time', 'direction', 'fromName', 'fromEmail', 'toNames', 'toEmails', 'subject', 'summary', 'body'],
        additionalProperties: false,
      },
    },
  },
  required: ['messages'],
  additionalProperties: false,
};

const SYSTEM = `Du delar upp mailkonversationer i enskilda mail åt ett svenskt säljteam på TIKAB.

Regler:
- Ett objekt per faktiskt mail i tråden, sorterat med äldsta först.
- Samma mail förekommer ofta flera gånger, citerat längst ned i senare svar. Ta med det EN gång.
- direction avgör vem som kontaktade vem: 'out' när avsändaren hör till TIKAB, 'in' när avsändaren hör till kunden eller någon annan utomstående.
- body ska vara mailets egen text i klartext. Ta bort mailsignaturer, kontaktuppgiftsblock, juridiska friskrivningar, "Skickat från min iPhone", spårningslänkar, bildplatshållare och den citerade historiken från tidigare mail. Behåll radbrytningar och punktlistor.
- summary är en mening på svenska som fångar vad mailet gällde och vad som beslutades.
- Hitta aldrig på uppgifter. Ett datum, en adress eller ett ämne som inte framgår lämnas som tom sträng.
- Underlaget kan vara rörigt (rå MIME, textsträngar ur en .msg-fil, avbrutna rader). Plocka ut det som faktiskt är mail och ignorera resten.`;

/**
 * @param {object} p
 * @param {string} p.text        Trådens råtext.
 * @param {string} p.ourEmail    Den inloggades adress — definierar TIKAB-sidan.
 * @param {string} p.ourName
 * @param {string} p.customerName
 * @param {Array}  p.contacts    [{ name, email }] hos kunden.
 * @returns {Promise<Array>} mailen i tråden
 */
export async function parseEmailThread({ text, ourEmail, ourName, customerName, contacts = [] }) {
  const ourDomain = (ourEmail || '').split('@')[1] || '';
  const known = contacts
    .filter(c => c.name || c.email)
    .map(c => `- ${c.name || 'okänt namn'}${c.email ? ` <${c.email}>` : ''}`)
    .join('\n');

  const context = [
    `Idag är det ${new Date().toISOString().split('T')[0]}. Saknas årtal, välj det år som ger ett datum i det närmaste förflutna.`,
    `TIKAB-sidan: ${ourName || 'den inloggade säljaren'} <${ourEmail || 'okänd adress'}>${ourDomain ? `, och alla adresser på @${ourDomain}` : ''}.`,
    customerName ? `Kunden i affären: ${customerName}.` : '',
    known ? `Kända kontaktpersoner hos kunden:\n${known}` : '',
  ].filter(Boolean).join('\n');

  const parsed = await callClaudeJSON({
    model: 'claude-opus-5',
    max_tokens: 32000,
    schema: THREAD_SCHEMA,
    system: SYSTEM,
    messages: [{
      role: 'user',
      content: `${context}\n\nMailtråd att dela upp:\n\n${text}`,
    }],
  });

  return (parsed.messages || []).filter(m => m.body?.trim() || m.summary?.trim());
}
